import { fetchEventSource } from '@microsoft/fetch-event-source';
import { fileRequest, getToken, request, streamUrl } from '../runtime';

/** 上传文件，返回 { url, name } */
export const _uploadFile = (file: File): Promise<{ url: string; name: string }> => {
  return fileRequest('/daedalus/chat/upload', { file: [file] });
};

/** 文件项 */
export interface FileItem {
  name?: string;
  url?: string;
}

/** 页面上下文 */
export interface PageContext {
  moduleId?: string;
  url?: string;
  pageTitle?: string;
  breadcrumb?: string[];
  /** 当前页面筛选条件 */
  filters?: Record<string, any>;
  /** 最近操作 */
  recentActions?: string[];
  /** 核心实体类型（如 experiment） */
  entityType?: string;
  /** 核心实体 ID */
  entityId?: string;
  /** 当前选中游戏 code（如 g79） */
  gameId?: string;
}

/** 请求级 skill 临时子集项（_chatSend.useSkills 用） */
export interface UseSkillItem {
  id?: number;
  name?: string;
}

/** /chat/send 返回结构 */
export interface ChatSendResponse {
  uuid: string;
  projectId: string;
}

/**
 * 发送聊天消息
 *
 * 现主发送路径为 `_chatSendStream`（流式）；本函数保留作为
 * `resumeStream` 缓存失效或流式二次失败后的**兜底通道**入口，
 * 与 `_chatPollWithStatus` / `startPolling` 联合使用。
 */
export const _chatSend = (params: {
  projectId?: string;
  message: string;
  files?: FileItem[];
  /** 本次请求指定模型 ID（优先级最高，为空走用户配置 → agent 默认） */
  modelId?: string;
  /** 本次请求 skill 临时子集，仅本次生效、纯透传不持久化；null/空=不限 */
  useSkills?: UseSkillItem[];
  context?: PageContext;
}): Promise<ChatSendResponse> => {
  return request('/daedalus/chat/send', params);
};

/** 快捷气泡 + 能力标签 */
export interface SuggestionItem {
  icon: string;
  text: string;
}
export interface ModuleSuggestionsVO {
  capabilities: string[];
  suggestions: SuggestionItem[];
}
export const _chatSuggestions = (params: {
  moduleId?: string;
  gameId?: string;
}): Promise<ModuleSuggestionsVO> => {
  return request('/daedalus/chat/suggestions', params);
};

/** Pipeline 附加的文档引用 */
export interface DocumentRef {
  documentId: number;
  title: string;
  docType: 'PLAN' | 'REPORT' | 'ANALYSIS' | 'SUMMARY' | 'OTHER';
  isNew: boolean;
}

/** 实体级动作信号：pollWithStatus 消息可携带，用于通知前端刷新对应实体 */
export interface EntityAction {
  /** 动作类型；目前仅识别 'refresh'，其它值前端静默忽略 */
  action: string;
  /** 实体类型，如 'experiment' */
  entityType?: string;
  /** 实体 ID */
  entityId?: string;
  /** 触发本信号的后端 tool 名，如 'saveOpenExp'，仅供排查 */
  tool?: string;
}

/** 聊天消息：1=USER_INPUT，2=AGENT_PROCESS（含思考过程），3=AGENT_RESULT */
export interface ChatMessageVO {
  id: string;
  type: 1 | 2 | 3;
  content: any;
  attachedDocuments?: DocumentRef[];
  createTime: string;
  /** 实体级动作信号；后端可能显式下发 null 表示"本条不挂信号" */
  entityAction?: EntityAction | null;
}

/**
 * 轮询执行过程（旧）
 * @deprecated 请使用 `_chatPollWithStatus`，新接口附带 `status` 与 token/费用统计
 */
export const _chatPoll = (params: { uuid: string; projectId: string }): Promise<ChatMessageVO[]> => {
  return request('/daedalus/chat/poll', params);
};

/** 轮询执行过程（带状态 + token/耗时/费用） */
export interface ChatPollWithStatusVO {
  status: 'IN_PROGRESS' | 'SUCCESS' | 'FAILED';
  uuid: string;
  messages: ChatMessageVO[];
  inputTokenCnt?: number;
  completeTokenCnt?: number;
  cacheTokenCnt?: number;
  tokenCount?: number;
  totalCost?: number | null;
  costMs?: number | null;
  modelId?: string;
  createTime?: string;
  finishTime?: string | null;
}
export const _chatPollWithStatus = (params: { uuid: string; projectId: string }): Promise<ChatPollWithStatusVO> => {
  return request('/daedalus/chat/pollWithStatus', params);
};

/** 查询聊天历史 */
export const _chatHistory = (params: { projectId: string }): Promise<ChatMessageVO[]> => {
  return request('/daedalus/chat/history', params);
};

/** session 维度统计 */
export interface ChatHistoryStats {
  uuidCount?: number;
  successCount?: number;
  failedCount?: number;
  inProgressCount?: number;
  totalInputTokenCnt?: number;
  totalCompleteTokenCnt?: number;
  totalCacheTokenCnt?: number;
  totalTokenCount?: number;
  totalCost?: number | null;
  totalCostMs?: number | null;
}

/** 按 uuid 分组的会话轮次 */
export interface ChatHistoryConversation {
  uuid: string;
  status: 'IN_PROGRESS' | 'SUCCESS' | 'FAILED';
  modelId?: string;
  inputTokenCnt?: number;
  completeTokenCnt?: number;
  cacheTokenCnt?: number;
  tokenCount?: number;
  totalCost?: number | null;
  costMs?: number | null;
  createTime?: string;
  finishTime?: string | null;
  messages: ChatMessageVO[];
}

/** /chat/history/detail 返回结构 */
export interface ChatHistoryDetailVO {
  agentId?: number;
  agentName?: string;
  agentIcon?: string;
  projectId: string;
  title?: string;
  channelType?: string;
  sourceType?: string;
  createTime?: string;
  updateTime?: string;
  stats?: ChatHistoryStats | null;
  conversations: ChatHistoryConversation[];
}

/**
 * 查询聊天历史详情：相比 `_chatHistory` 的扁平消息列表，额外返回 session 元信息、
 * session 维度的 token/费用统计（`stats`），以及按 uuid 分组的会话轮次（`conversations`）。
 */
export const _chatHistoryDetail = (params: { projectId: string }): Promise<ChatHistoryDetailVO> => {
  return request('/daedalus/chat/history/detail', params);
};

/** 打断对话：终止指定项目当前正在执行的 Agent 对话轮次 */
export const _chatInterrupt = (params: { projectId: string }) => {
  return request('/daedalus/chat/interrupt', params);
};

/** 会话项（上游分页结构，按需取，其余字段透传） */
export interface SessionItem {
  sessionId: string;
  projectId?: string;
  title?: string;
  createTime?: string;
  [key: string]: any;
}
export interface ChatSessionListVO {
  list: SessionItem[];
  total: number;
  pageNum?: number;
  pageSize?: number;
}
/** 会话列表（分页） */
export const _chatSessionList = (params: { pageNum?: number; pageSize?: number }): Promise<ChatSessionListVO> => {
  return request('/daedalus/chat/sessionList', params);
};

/** 删除会话（上游软删除，本人校验） */
export const _chatSessionDelete = (params: { sessionId: string }): Promise<void> => {
  return request('/daedalus/chat/sessionDelete', params);
};

/** 模型项 */
export interface ModelItemVO {
  modelId: string;
  modelName: string;
}
/** 模型供应商 */
export interface ModelProviderVO {
  value: string;
  label: string;
  models: ModelItemVO[];
}
/** 模型清单（无 body） */
export const _chatModelList = (): Promise<ModelProviderVO[]> => {
  return request('/daedalus/chat/modelList', {});
};

/** 当前用户 Agent 配置（未配置项为 null） */
export interface ChatConfigVO {
  modelId: string | null;
  mcpIds: number[] | null;
  /** 历史字段，等同 `personalSkillIds`，保留以兼容既有调用点 */
  skillIds: number[] | null;
  /** 公用技能：Agent 默认绑定的 Skill ID 列表（来自上游 `/magicAgent/detail`），用户不可增删 */
  sharedSkillIds: number[] | null;
  /** 个人技能：用户通过 `_chatUpdateConfig` 管理的 Skill ID 列表 */
  personalSkillIds: number[] | null;
}

/**
 * 获取当前用户 Agent 配置（模型 / mcp / skill）
 *
 * 与 `_chatUpdateConfig` 配对使用：先 getConfig 读当前值，再 updateConfig 做 partial 修改。
 * 未配置项返回 `null`（保持原样不要转 undefined / 空数组）。
 *
 * 返回字段（详见 `apidocs/ChatController.md §10`）：
 * - `modelId`：当前配置模型标识
 * - `mcpIds`：MCP Server ID 列表
 * - `skillIds`：历史字段，等同 `personalSkillIds`
 * - `sharedSkillIds`：公用技能（Agent 默认绑定，只读）
 * - `personalSkillIds`：个人技能（用户可增删）
 *
 * 上游契约保证 `sharedSkillIds` 与 `personalSkillIds` 不相交，前端不做额外去重。
 */
export const _chatGetConfig = (): Promise<ChatConfigVO> => {
  return request('/daedalus/chat/getConfig', {});
};

/**
 * 更新配置（partial：传什么改什么）
 * - 切模型：`{ modelId }`
 * - 配 skills：`{ skillIds }`（`[]` 清空）
 * - 同时改：`{ modelId, skillIds }`
 *
 * 注意：modelId 与 skillIds 不能同时为空。
 */
export const _chatUpdateConfig = (params: { modelId?: string; skillIds?: number[] }): Promise<void> => {
  return request('/daedalus/chat/updateConfig', params);
};

/** 创建分享 */
export interface ChatShareCreateVO {
  shareId: string;
}
export const _chatShareCreate = (params: { projectId: string; messageIds: string[] }): Promise<ChatShareCreateVO> => {
  return request('/daedalus/chat/share/create', params);
};

/** 查询分享内容（结构同 history） */
export const _chatShareGet = (params: { shareId: string }): Promise<ChatMessageVO[]> => {
  return request('/daedalus/chat/share/get', params);
};

/** 技能项管理员 */
export interface SkillAdminItem {
  userName?: string;
  displayName?: string;
  isCreator?: boolean;
}
/** 当前用户对 Skill 的角色 */
export type SkillRole = 'USER' | 'CREATED' | 'MANAGED';
/** 技能项（透传未约定字段） */
export interface SkillItem {
  id: number;
  title?: string;
  name?: string;
  description?: string;
  iconUrl?: string;
  tags?: string[];
  category?: string;
  creatorName?: string;
  creatorId?: string;
  createTime?: string;
  status?: string;
  isFeatured?: boolean;
  refCount?: number;
  invokeCount?: number;
  currentVersion?: number;
  admins?: SkillAdminItem[];
  authorized?: boolean;
  role?: SkillRole | string;
  [key: string]: any;
}
export interface SkillListVO {
  list: SkillItem[];
  total: number;
  pageNum?: number;
  pageSize?: number;
}
/** 技能浏览列表（分页搜索） */
export const _skillList = (params?: {
  keyword?: string;
  featured?: boolean;
  categories?: string[];
  sortBy?: 'LATEST' | 'REF_COUNT';
  authFilter?: 'AUTHORIZED' | 'UNAUTHORIZED';
  onlyMine?: boolean;
  roleFilter?: 'ALL' | 'CREATED' | 'MANAGED';
  statusFilter?: 'ALL' | 'DRAFT' | 'PUBLISHED';
  pageNum?: number;
  pageSize?: number;
}): Promise<SkillListVO> => {
  return request('/daedalus/skill/list', params ?? {});
};

/** 技能分类项 */
export interface SkillCategoryItem {
  code: string;
  displayName: string;
  iconUrl?: string;
}
/** 技能分类枚举列表 */
export const _skillCategories = (): Promise<{ categories: SkillCategoryItem[] }> => {
  return request('/daedalus/skill/categories', {});
};

/** 业内精品白名单（结构同 _skillList 返回） */
export const _skillPickerFeatured = (): Promise<SkillListVO> => {
  return request('/daedalus/skill/picker/featured', {});
};

/** LLM 自动推荐技能（无 body，后端使用固定 agentId） */
export const _skillPickerRecommend = (): Promise<SkillListVO> => {
  return request('/daedalus/skill/picker/recommend', {});
};

/** Skill 关联 Agent */
export interface SkillRelatedAgent {
  agentId: number;
  agentName: string;
}
/** Skill 关联文件 */
export interface SkillFileItem {
  id: number;
  filePath?: string;
  fileUrl?: string;
  fileSize?: number;
  contentType?: string;
  fileContent?: string;
  isMainFile?: boolean;
  createTime?: string;
}
/** Skill 版本摘要 */
export interface SkillVersionSummary {
  version: number;
  status?: string;
  createTime?: string;
  creatorName?: string;
}
/** Skill 详情（/skill/detail/batch 返回项） */
export interface SkillDetailVO {
  id: number;
  title?: string;
  name?: string;
  description?: string;
  iconUrl?: string;
  tags?: string[];
  category?: string;
  creatorName?: string;
  creatorId?: string;
  createTime?: string;
  updateTime?: string;
  status?: string;
  sourceUrl?: string;
  isFeatured?: boolean;
  adminUserNames?: string[];
  files?: SkillFileItem[];
  currentVersion?: number | null;
  versionHistory?: SkillVersionSummary[];
  invokeCount?: number;
  referenceCount?: number;
  relatedAgents?: SkillRelatedAgent[];
  authorized?: boolean;
  role?: SkillRole | string;
  [key: string]: any;
}
/** 批量查询技能详情（idList 不能为空） */
export const _skillDetailBatch = (params: { idList: number[] }): Promise<SkillDetailVO[]> => {
  return request('/daedalus/skill/detail/batch', params);
};

/** 文档列表 */
export interface DocumentVO {
  id: number;
  title: string;
  docType: string;
  createTime: string;
  updateTime: string;
}
export const _documentList = (params: { projectId: string }) => {
  return request('/daedalus/documents/list', params);
};

/** 项目列表 */
export interface BoundEntityVO {
  entityType: string;
  entityId: string;
  label: string;
}
export interface ProjectVO {
  projectId: string;
  title: string;
  boundEntity: BoundEntityVO | null;
  tags: string[];
  createTime: string;
  updateTime: string;
}
export const _projectList = (params?: { projectType?: string }): Promise<ProjectVO[]> => {
  return request('/daedalus/projects/list', params ?? {});
};

/** 更新项目 */
export interface ProjectUpdateRequest {
  projectId: string;
  title?: string;
  tags?: string[];
  status?: 0 | 1;
}
export const _projectUpdate = (params: ProjectUpdateRequest) => {
  return request('/daedalus/projects/update', params);
};

/** 删除项目（owner 校验，级联删除上游 agent 会话 + 本地 project / 文档） */
export const _projectDelete = (params: { projectId: string }): Promise<void> => {
  return request('/daedalus/projects/delete', params);
};

/** 获取文档内容 */
export interface DocumentContentVO {
  id: number;
  title: string;
  docType: string;
  content: string;
}
export const _documentContent = (params: { documentId: number }) => {
  return request('/daedalus/documents/content', params);
};

/** OIDC：获取网易登录授权跳转地址（GET） */
export interface OidcAuthorizeUrlVO {
  authorizeUrl: string;
}
export const _oidcAuthorizeUrl = (): Promise<OidcAuthorizeUrlVO> => {
  return request('/daedalus/oidc/authorize-url', {}, { method: 'get' });
};

/** OIDC：用 code+state 换取后端凭证 */
export const _oidcExchange = (params: { code: string; state: string }) => {
  return request('/daedalus/oidc/exchange', params);
};

/* ────────────────────────────────────────────────────────────────
 *  流式对话（SSE）— sendStream / resumeStream
 *  接口协议详见 apidocs/前端流式对话接入指南.md 与 ChatController.md §1.1 / §1.2
 *  流式接口不走 ResData / axios 拦截器，错误通过 onError 回调或 promise reject 抛出。
 * ──────────────────────────────────────────────────────────────── */

/** sendStream / resumeStream 共享的 body（同 /chat/send） */
export interface ChatSendRequest {
  projectId?: string;
  message: string;
  files?: FileItem[];
  modelId?: string;
  useSkills?: UseSkillItem[];
  context?: PageContext;
  /** askQuestion 作答回传：单对象或数组（一轮多 questionId 时）；纯透传 */
  questionAnswers?: QuestionAnswerPayload | QuestionAnswerPayload[];
}

/** askQuestion：单个选项（label 必有，description 可选） */
export interface QuestionOption {
  label: string;
  description?: string;
}

/** askQuestion：单个子问题 */
export interface QuestionItem {
  question: string;
  header: string;
  multiSelect: boolean;
  options: QuestionOption[];
}

/** askQuestion：Agent 抛出的问题数据（来自含 content.questionId 的消息） */
export interface QuestionData {
  questionId: string;
  questions: QuestionItem[];
}

/** askQuestion：用户作答载荷（answers[i] 按位置对应 questions[i]） */
export interface QuestionAnswerPayload {
  questionId: string;
  answers: Array<{
    header: string;
    question?: string;
    selected: string[];
  }>;
}

/** accept 帧 payload：daedalus 在此帧注入 projectId（首轮自动建项目的新 ID 亦在此） */
export interface AgentStreamAccept {
  sessionId: string;
  uuid: string;
  modelId: string | null;
  projectId: string;
}

/** SSE 单帧的判别联合类型 */
export type ChatStreamFrame =
  | { event: 'accept'; data: AgentStreamAccept }
  | { event: 'text_chunk'; data: string }
  | { event: 'thinking_chunk'; data: string }
  | { event: 'reasoning'; data: ChatMessageVO }
  | { event: 'tool_result'; data: ChatMessageVO }
  | { event: 'result'; data: ChatMessageVO }
  | { event: 'finish'; data: ChatPollWithStatusVO }
  | { event: 'error'; data: unknown };

/** 单帧 meta：透传 SSE `id:` 游标供业务侧记录 lastEventId */
export interface StreamFrameMeta {
  id?: string;
}

/** 归一化的流式错误：OIDC 错误由 isOidcRequired 判别 */
export interface StreamError {
  desc: string;
  isOidcRequired?: boolean;
  raw?: unknown;
}

/** 帧派发回调集 */
export interface StreamHandlers {
  onAccept?: (data: AgentStreamAccept, meta: StreamFrameMeta) => void;
  onTextChunk?: (chunk: string, meta: StreamFrameMeta) => void;
  onThinkingChunk?: (chunk: string, meta: StreamFrameMeta) => void;
  onReasoning?: (data: ChatMessageVO, meta: StreamFrameMeta) => void;
  onToolResult?: (data: ChatMessageVO, meta: StreamFrameMeta) => void;
  onResult?: (data: ChatMessageVO, meta: StreamFrameMeta) => void;
  onFinish?: (data: ChatPollWithStatusVO, meta: StreamFrameMeta) => void;
  onError?: (err: StreamError) => void;
}

/**
 * 服务层内部错误：`error` 帧到达后抛出此实例，用于中断 fetchEventSource 派发。
 * 业务层不应直接感知（onError 已同步派发一次）。
 */
class FatalStreamError extends Error {
  readonly __fatal = true as const;
  constructor() {
    super('stream terminated by error frame');
  }
}

/** 解析单帧 SSE `data:` JSON。无效 JSON / event 未知 → 返回 null（不中断流） */
const parseFrame = (rawData: string): ChatStreamFrame | null => {
  if (!rawData) return null;
  let parsed: any;
  try {
    parsed = JSON.parse(rawData);
  } catch {
    console.warn('[chat-stream] invalid frame JSON', rawData);
    return null;
  }
  const event = parsed?.event;
  const data = parsed?.data;
  switch (event) {
    case 'accept':
    case 'text_chunk':
    case 'thinking_chunk':
    case 'reasoning':
    case 'tool_result':
    case 'result':
    case 'finish':
    case 'error':
      return { event, data } as ChatStreamFrame;
    default:
      console.warn('[chat-stream] unknown event', event);
      return null;
  }
};

/** 将 `error` 帧 payload 归一化为 StreamError；同时识别 OIDC */
const mapErrorFrame = (payload: unknown): StreamError => {
  if (typeof payload === 'string') {
    const isOidc = payload.includes('用户未登录') || payload.includes('需要 OIDC 授权');
    return { desc: payload, isOidcRequired: isOidc, raw: payload };
  }
  if (payload && typeof payload === 'object') {
    const obj = payload as Record<string, any>;
    const desc: string = typeof obj.desc === 'string' ? obj.desc : '流式对话失败';
    const explicitOidc = obj.isOidcRequired === true;
    const descOidc = desc.includes('用户未登录') || desc.includes('需要 OIDC 授权');
    return { desc, isOidcRequired: explicitOidc || descOidc, raw: payload };
  }
  return { desc: '流式对话失败', raw: payload };
};

/** 逐帧派发到 handlers（内部使用；resume / send 复用） */
const dispatchFrame = (frame: ChatStreamFrame, meta: StreamFrameMeta, handlers: StreamHandlers): void => {
  switch (frame.event) {
    case 'accept':
      handlers.onAccept?.(frame.data, meta);
      return;
    case 'text_chunk':
      handlers.onTextChunk?.(frame.data, meta);
      return;
    case 'thinking_chunk':
      handlers.onThinkingChunk?.(frame.data, meta);
      return;
    case 'reasoning':
      handlers.onReasoning?.(frame.data, meta);
      return;
    case 'tool_result':
      handlers.onToolResult?.(frame.data, meta);
      return;
    case 'result':
      handlers.onResult?.(frame.data, meta);
      return;
    case 'finish':
      handlers.onFinish?.(frame.data, meta);
      return;
    case 'error':
      handlers.onError?.(mapErrorFrame(frame.data));
      throw new FatalStreamError();
  }
};

/** 组装 SSE 请求头（对齐 axios 请求头：Authorization + Accept + Content-Type） */
const buildStreamHeaders = (extra?: Record<string, string>): Record<string, string> => {
  const auth = getToken() || '';
  return {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
    Authorization: auth,
    ...(extra || {})
  };
};

/**
 * 打开一次 SSE，逐帧派发到 handlers。
 * - 正常 `finish` 或本地 `abort()` → promise resolve
 * - 网络异常（onerror）→ promise reject（业务层可据此触发 resumeStream）
 * - `error` 帧 → onError 同步派发一次，随后 promise resolve（业务错误已消化）
 */
const openStream = (
  url: string,
  body: object,
  handlers: StreamHandlers,
  signal: AbortSignal,
  extraHeaders?: Record<string, string>
): Promise<void> => {
  return new Promise<void>((resolve, reject) => {
    fetchEventSource(url, {
      method: 'POST',
      headers: buildStreamHeaders(extraHeaders),
      body: JSON.stringify(body),
      credentials: 'include',
      signal,
      openWhenHidden: true,
      onmessage(ev) {
        const meta: StreamFrameMeta = ev.id ? { id: ev.id } : {};
        const frame = parseFrame(ev.data);
        if (!frame) return;
        try {
          dispatchFrame(frame, meta, handlers);
        } catch (err) {
          if (err instanceof FatalStreamError) {
            resolve();
            throw err;
          }
          throw err;
        }
      },
      onclose() {
        resolve();
      },
      onerror(err) {
        if (err instanceof FatalStreamError) throw err;
        reject(err);
        throw err;
      }
    }).catch((err) => {
      if (err instanceof FatalStreamError) return;
      // 已由 onerror reject；此 catch 只吞掉 fetchEventSource 本身向外抛的错误
    });
  });
};

/** 发起流式对话（POST /chat/sendStream） */
export const _chatSendStream = (
  params: ChatSendRequest,
  handlers: StreamHandlers,
  signal: AbortSignal
): Promise<void> => {
  return openStream(streamUrl('/daedalus/chat/sendStream'), params, handlers, signal);
};

/**
 * 流式续推（POST /chat/resumeStream）。
 * 传入 `lastEventId` 时以 `Last-Event-ID` 请求头下发，供后端从游标续推。
 */
export const _chatResumeStream = (
  params: { uuid: string; projectId: string; lastEventId?: string },
  handlers: StreamHandlers,
  signal: AbortSignal
): Promise<void> => {
  const { lastEventId, ...body } = params;
  const extra = lastEventId ? { 'Last-Event-ID': lastEventId } : undefined;
  return openStream(streamUrl('/daedalus/chat/resumeStream'), body, handlers, signal, extra);
};
